import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle, 
  Circle, 
  MapPin, 
  UserPlus, 
  Settings, 
  Clock, 
  ArrowRight, 
  X,
  Sparkles,
  Building,
  Users,
  Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GettingStartedWizard = ({ user, onDismiss, onComplete }) => {
  const navigate = useNavigate();
  const [setupStatus, setSetupStatus] = useState({
    has_office: false,
    has_employees: false,
    has_settings: false,
    has_clocked_in: false,
    loading: true
  });
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Check for offices
      const officesRes = await axios.get(`${API}/offices`, { headers });
      const hasOffice = officesRes.data.length > 0;

      // Check for employees (excluding the admin themselves)
      const employeesRes = await axios.get(`${API}/employees`, { headers });
      const hasEmployees = employeesRes.data.filter(e => e.role === 'employee').length > 0;

      // Check for company settings customization
      const settingsRes = await axios.get(`${API}/settings/company`, { headers });
      const hasSettings = settingsRes.data?.break_settings?.breakfast_duration !== 30 || 
                         settingsRes.data?.break_settings?.lunch_duration !== 60;

      // Check if admin has clocked in
      const currentRes = await axios.get(`${API}/time-entries/current`, { headers });
      const hasClocked = currentRes.data?.active_entry !== null;

      setSetupStatus({
        has_office: hasOffice,
        has_employees: hasEmployees,
        has_settings: hasSettings,
        has_clocked_in: hasClocked,
        loading: false
      });

      // If all steps complete, trigger onComplete
      if (hasOffice && hasEmployees) {
        // Basic setup is complete
      }
    } catch (error) {
      console.error('Error checking setup status:', error);
      setSetupStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const steps = [
    {
      id: 'office',
      title: 'Set Up Your Office',
      description: 'Create a geofenced work location where employees can clock in',
      icon: MapPin,
      completed: setupStatus.has_office,
      action: () => navigate('/admin/offices'),
      actionText: 'Add Office',
      priority: 'required'
    },
    {
      id: 'employees',
      title: 'Invite Your Team',
      description: 'Send invitations to your employees to join ClockIn Pro',
      icon: UserPlus,
      completed: setupStatus.has_employees,
      action: () => navigate('/admin/employees'),
      actionText: 'Invite Employees',
      priority: 'required'
    },
    {
      id: 'settings',
      title: 'Configure Settings',
      description: 'Customize break times, overtime rules, and notifications',
      icon: Settings,
      completed: setupStatus.has_settings,
      action: () => navigate('/admin/settings'),
      actionText: 'Open Settings',
      priority: 'optional'
    },
    {
      id: 'clockin',
      title: 'Try Clocking In',
      description: 'Test the system yourself to see how it works',
      icon: Clock,
      completed: setupStatus.has_clocked_in,
      action: () => navigate('/employee'),
      actionText: 'My Clock-In',
      priority: 'optional'
    }
  ];

  const completedSteps = steps.filter(s => s.completed).length;
  const requiredSteps = steps.filter(s => s.priority === 'required');
  const completedRequired = requiredSteps.filter(s => s.completed).length;
  const progress = Math.round((completedSteps / steps.length) * 100);

  const handleDismiss = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/dismiss-wizard`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silently fail - wizard will still be dismissed locally
    }
    onDismiss?.();
  };

  if (setupStatus.loading) {
    return null;
  }

  // All required steps complete - show success state briefly
  if (completedRequired === requiredSteps.length && !isMinimized) {
    return (
      <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 shadow-lg" data-testid="wizard-complete">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-green-800">You're all set!</h3>
              <p className="text-sm text-green-600">
                Your company is ready to track time. Your team can now clock in!
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleDismiss}
              className="text-green-600 hover:text-green-700"
            >
              Dismiss
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isMinimized) {
    return (
      <Card 
        className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setIsMinimized(false)}
        data-testid="wizard-minimized"
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <Zap className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-indigo-800">Setup Progress: {progress}%</p>
              <Progress value={progress} className="h-2 mt-1" />
            </div>
            <Badge variant="outline" className="bg-indigo-100 text-indigo-700 border-indigo-200">
              {completedSteps}/{steps.length}
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-indigo-200 shadow-lg overflow-hidden" data-testid="wizard-expanded">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Welcome to ClockIn Pro!</h2>
              <p className="text-indigo-100 text-sm">Let's get your company set up in just a few steps</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            className="text-white/70 hover:text-white hover:bg-white/10"
            onClick={() => setIsMinimized(true)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Progress */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-indigo-100">Setup Progress</span>
            <span className="text-sm font-medium">{completedSteps} of {steps.length} complete</span>
          </div>
          <Progress value={progress} className="h-2 bg-white/20" />
        </div>
      </div>

      {/* Steps */}
      <CardContent className="p-0">
        <div className="divide-y">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isNext = !step.completed && steps.slice(0, index).every(s => s.completed || s.priority === 'optional');
            
            return (
              <div 
                key={step.id}
                className={`p-4 flex items-center gap-4 transition-colors ${
                  step.completed ? 'bg-green-50/50' : isNext ? 'bg-indigo-50/50' : ''
                }`}
                data-testid={`wizard-step-${step.id}`}
              >
                {/* Step indicator */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.completed 
                    ? 'bg-green-100 text-green-600' 
                    : isNext 
                      ? 'bg-indigo-100 text-indigo-600'
                      : 'bg-slate-100 text-slate-400'
                }`}>
                  {step.completed ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>

                {/* Step content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-medium ${step.completed ? 'text-green-700' : ''}`}>
                      {step.title}
                    </h3>
                    {step.priority === 'required' && !step.completed && (
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        Required
                      </Badge>
                    )}
                    {step.completed && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        Done
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-0.5">{step.description}</p>
                </div>

                {/* Action button */}
                {!step.completed && (
                  <Button
                    size="sm"
                    variant={isNext ? 'default' : 'outline'}
                    className={isNext ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                    onClick={step.action}
                    data-testid={`wizard-action-${step.id}`}
                  >
                    {step.actionText}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Complete the required steps to unlock all features
          </p>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleDismiss}
            className="text-slate-500"
          >
            Skip for now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default GettingStartedWizard;
